import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import {
  ArrowRight,
  Phone,
  Mail,
  MessageCircle,
  Menu,
  Sparkles,
  Star,
  Shield,
  Award,
  Clock,
  Users,
  CheckCircle,
  Quote,
  Instagram,
  MapPin,
  Stethoscope,
  Ear,
  Wind,
  Brain,
  Activity,
  Heart,
} from 'lucide-react';
import { FaInstagram } from 'react-icons/fa';
import LogoImage from '../assets/images/logo.png';
// Placeholder — replace with actual ENT doctor image:
import ENTDoctorImage from '../assets/images/ENTExpert.jpg';
import { TestimonialCard } from '../components/TestimonialCard';
import './ENTPage.css';

const ENTPage = () => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const navigate = useNavigate();

  const handleBookENT = () => {
    const phoneNumber = '917887878109';
    const message = 'Hello! I would like to book an ENT consultation with Dr. Sourabh Kosalge.';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const scrollToSection = (sectionId: string) => {
    setSheetOpen(false);
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const stats = [
    { icon: Users,  value: '30,000+', label: 'Patients Treated' },
    { icon: Shield, value: '2,000+',  label: 'Surgeries Performed' },
    { icon: Clock,  value: '5+',      label: 'Years Experience' },
    { icon: Award,  value: '2',        label: 'Advanced Fellowships' },
  ];

  const specialties = [
    {
      icon: Wind,
      title: 'Sinusitis & Nasal Blockage',
      description: 'Chronic congestion, sinus headaches, nasal polyps, and deviated septum — treated with evidence-based protocols.',
      features: ['Chronic sinusitis management', 'Nasal polyp treatment', 'Deviated septum evaluation', 'Allergy-triggered sinus care'],
    },
    {
      icon: Activity,
      title: 'Allergic Rhinitis',
      description: 'Stop managing symptoms and treat the root cause of morning sneezing, runny nose, and persistent nasal inflammation.',
      features: ['Seasonal & perennial allergies', 'Allergen identification', 'Immunotherapy guidance', 'Medication optimisation'],
    },
    {
      icon: Ear,
      title: 'Hearing & Balance Disorders',
      description: 'Specialist care for vertigo, tinnitus, hearing loss, and balance issues rooted in inner ear conditions.',
      features: ['Vertigo & BPPV management', 'Tinnitus evaluation', 'Hearing loss assessment', 'Eustachian tube dysfunction'],
    },
    {
      icon: Heart,
      title: 'Throat & Voice Disorders',
      description: 'Expert diagnosis and treatment for sore throat, hoarseness, tonsillitis, snoring, and sleep-related breathing issues.',
      features: ['Chronic tonsillitis', 'Hoarseness & voice changes', 'Snoring & sleep apnoea screening', 'Throat infection management'],
    },
  ];

  const testimonials = [
    {
      name: 'Shubhangi Shinde',
      quote: 'Dr. Sourabh, explained the condition clearly and patiently.He prescribed effective treatment, I started feeling relief within few days.The clinic was well managed and staff were courteous.Highly recommended for sinus and ENT problems',
      rating: 5,
    },
    {
      name: 'Anuja Ganjewar',
      quote: 'I got significant relief from tinnitus after the treatment from Dr. Kosalge. The procedure was smooth and the overall experience',
      rating: 5,
    },
    {
      name: 'Ashish Waghmare',
      quote: 'My wife Usha underwent ear surgery under Dr. Sourabh. He did his job pretty smoothly',
      rating: 5,
    },
  ];

  const faqs = [
    {
      q: 'What conditions does Dr. Kosalge treat?',
      a: 'Dr. Kosalge specialises in all ENT conditions including sinusitis, allergic rhinitis, hearing loss, vertigo, tinnitus, throat infections, tonsillitis, snoring, and facial trauma. He also consults on pre-surgical assessment for ENT procedures.',
    },
    {
      q: 'How does the online ENT consultation work?',
      a: 'After booking via WhatsApp, you will receive a link to a secure video or voice consultation. Dr. Kosalge will review your symptoms, history, and any relevant reports, then provide a detailed diagnosis, digital prescription, and treatment plan.',
    },
    {
      q: 'Will I receive a prescription after my consultation?',
      a: 'Yes. Every consultation includes a digital prescription that you can use at any pharmacy, also provide guidance relevant to your condition.',
    },
    {
      q: 'What if I need a physical examination or surgery?',
      a: 'Dr. Kosalge will advise you clearly if your condition requires an in-person visit or surgical intervention. He can guide you on the appropriate next steps.',
    },
    {
      q: 'Is the ₹999 consultation fee refundable?',
      a: 'Please refer to our Cancellation & Refund policy on the Legal page. In general, confirmed consultations are non-refundable, but we will work with you if there are any technical issues on our end.',
    },
  ];

  const connects = [
    {
      icon: MessageCircle,
      label: 'WhatsApp',
      value: '+91 7887878109',
      action: handleBookENT,
      cta: 'Chat & Book',
      color: 'bg-green-50 text-green-700',
    },
    {
      icon: Phone,
      label: 'Call Us',
      value: '+91 7887878109',
      action: () => window.open('tel:+917887878109'),
      cta: 'Call Now',
      color: 'bg-[#f9f1e8] text-[#d4a574]',
    },
    {
      icon: MapPin,
      label: 'Visit Us',
      value: 'Kommawar Hospital, doctor line, Nanded',
      action: () => window.open('https://maps.app.goo.gl/N2ropaz1wJXFRP9f7', '_blank'),
      cta: 'Get Directions',
      color: 'bg-[#f9f1e8] text-[#d4a574]',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'Saurabh.kosalge77@gmail.com',
      action: () => window.open('mailto:Saurabh.kosalge77@gmail.com'),
      cta: 'Send Email',
      color: 'bg-[#f9f1e8] text-[#d4a574]',
    },
    {
      icon: Instagram,
      label: 'Instagram',
      value: '@dr_sourabhkosalgeent',
      action: () => window.open('https://www.instagram.com/dr_sourabhkosalgeent', '_blank'),
      cta: 'Follow Us',
      color: 'bg-pink-50 text-pink-600',
    },
  ];

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">

      {/* Top Banner */}
      <div className="bg-gradient-to-r from-[#d4a574] to-[#c89464] text-white py-2 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center md:justify-between gap-4 text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+918788631381" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Phone className="w-4 h-4" />
              <span>+91 8788631381</span>
            </a>
            <a href="mailto:clearandbare.skincare@gmail.com" className="hidden sm:flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Mail className="w-4 h-4" />
              <span>clearandbare.skincare@gmail.com</span>
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span>ENT Consultations — Now Available</span>
            <span>Starting at only ₹999</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-[#d4a574] to-[#c89464] rounded-full flex items-center justify-center overflow-hidden">
                <img src={LogoImage} alt="Clear&Bare" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-[#2c2c2c] tracking-tight leading-none">Clear & Bare</h1>
                <p className="text-xs text-[#999]">Skincare & ENT Simplified</p>
              </div>
            </motion.div>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-8">
              <a href="/" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Home
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300" />
              </a>
              <a href="/#experts" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Our Experts
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300" />
              </a>
              <a href="/shop" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Shop
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300" />
              </a>
              <a href="/contact" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Contact
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300" />
              </a>
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <Button
                onClick={handleBookENT}
                className="bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full px-6 shadow-lg shadow-[#d4a574]/30 transition-all duration-300"
              >
                Book ENT Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Mobile Drawer */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6 text-[#2c2c2c]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-white">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#d4a574] to-[#c89464] rounded-full flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-[#2c2c2c]">Clear & Bare</div>
                        <div className="text-xs text-[#999]">Skincare & ENT</div>
                      </div>
                    </div>
                  </SheetTitle>
                </SheetHeader>
                <Separator className="my-6" />
                <nav className="flex flex-col gap-1">
                  {[
                    { label: 'Home', href: '/' },
                    { label: 'About Dr. Kosalge', id: 'about' },
                    { label: 'Specialties', id: 'specialties' },
                    { label: 'Testimonials', id: 'testimonials' },
                    { label: 'Contact & Book', id: 'connect' },
                  ].map((item) => (
                    <button
                      key={item.label}
                      onClick={() => item.href ? navigate(item.href) : scrollToSection(item.id!)}
                      className="flex items-center justify-between px-4 py-3 text-[#666] hover:bg-[#faf8f5] hover:text-[#d4a574] rounded-lg transition-all group text-left"
                    >
                      <span>{item.label}</span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </nav>
                <Separator className="my-6" />
                <Button
                  onClick={handleBookENT}
                  className="w-full bg-gradient-to-r from-[#d4a574] to-[#c89464] text-white rounded-full"
                >
                  Book ENT Consultation
                </Button>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section id="home" className="py-20 bg-gradient-to-br from-[#faf8f5] to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4a574]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#c89464]/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <Badge className="mb-6 bg-[#f9f1e8] text-[#d4a574] border-0 px-8 py-1">
                ✦ Now Available — ENT Care
              </Badge>
              <h1 className="text-5xl md:text-6xl text-[#2c2c2c] mb-6 leading-tight">
                Expert ENT Care,<br />
                <span className="text-[#d4a574]">Online</span>
              </h1>
              <p className="text-[#666] text-lg leading-relaxed mb-8">
                Consult with Dr. Sourabh Kosalge — MBBS, MS (ENT), Senior Fellow from Mumbai — for
                sinus, allergy, hearing, vertigo, and throat conditions. Evidence-based treatment
                from the comfort of home.
              </p>

              <div className="flex flex-wrap gap-3 mb-10">
                {['Sinusitis', 'Allergic Rhinitis', 'Vertigo', 'Hearing Loss', 'Snoring', 'Throat Disorders', 'Ear Infections', 'Voice Disorder'].map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-1.5 bg-white border border-[#e0ddd8] rounded-full text-sm text-[#666]"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={handleBookENT}
                  className="bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-[#d4a574]/30 transition-all duration-300 group"
                >
                  Book ENT Consultation — ₹999
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => scrollToSection('about')}
                  className="rounded-full px-8 py-6 text-lg border-[#e0ddd8] text-[#666] hover:border-[#d4a574] hover:text-[#d4a574]"
                >
                  About Dr. Kosalge
                </Button>
              </div>
            </motion.div>

            {/* Doctor Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-3xl p-8 shadow-2xl shadow-[#d4a574]/10 border border-gray-100">
                {/* Avatar placeholder — replace <div> with <img> once you have the doctor photo */}
                <div className="w-full h-64 bg-gradient-to-br from-[#f9f1e8] to-[#faf8f5] rounded-2xl flex items-center justify-center mb-6 overflow-hidden">
                  {<img src={ENTDoctorImage} alt="Dr. Sourabh Kosalge" className="w-full h-full object-cover rounded-2xl" /> }                  
                </div>

                <div className="text-center mb-6">
                  <h2 className="text-2xl text-[#2c2c2c] mb-1">Dr. Sourabh Kosalge</h2>
                  <p className="text-[#d4a574] mb-2">MBBS, MS(ENT) – Government Medical College & Ghati Hospital</p>
                  <p className="text-[#d4a574] mb-2">Senior Fellow – Shatabdi Hospital, Mumbai</p>
                  <p className="text-[#d4a574] mb-2">Fellowship in Facial Trauma – Mumbai</p>
                  <p className="text-[#d4a574] mb-2">Fellowship in Vertigo – Bangalore</p>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-100">
                  {[
                   
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <div className="text-[#d4a574] text-xl font-semibold">{s.value}</div>
                      <div className="text-[#999] text-xs mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-[#d4a574] to-[#c89464] text-white px-4 py-2 rounded-full text-sm shadow-lg">
                Starting ₹999
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="py-12 bg-[#2c2c2c]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <s.icon className="w-6 h-6 text-[#d4a574] mx-auto mb-3" />
                <div className="text-3xl text-white mb-1">{s.value}</div>
                <div className="text-[#999] text-sm">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-20 bg-white min-h-screen">
         <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
               initial={{ opacity: 0, x: -30 }}
               whileInView={{ opacity: 1, x: 0 }}
               viewport={{ once: true }}
               className="space-y-4 lg:sticky lg:top-24"
            >
            <Badge className="mb-6 bg-[#f9f1e8] text-[#d4a574] border-0 px-4 py-1">About the Doctor</Badge>
              <h2 className="text-4xl text-[#2c2c2c] mb-6">
                 Meet Dr. Sourabh Kosalge
              </h2>

              <p className="text-[#666] leading-relaxed mb-5 max-w-xl">
                Dr. Sourabh Kosalge is a highly skilled ENT and Head & Neck Surgeon dedicated to providing advanced, evidence-based medical care. He combines a rigorous academic background from premier institutions with elite, specialized fellowships in facial trauma and vertigo. Driven by a patient-first philosophy, Dr. Kosalge utilizes modern, minimally invasive techniques to deliver precise diagnoses and tailored treatments for complex ear, nose, throat, and head & neck conditions.
              </p>
              <p className="text-[#666] leading-relaxed mb-5 max-w-xl">
                He brings together cutting-edge diagnostic expertise and compassionate, patient-first
                care. Whether you are dealing with a simple ear infection or a complex vestibular disorder,
                Dr. Sourabh provides definitive clinical care with clarity and precision.
              </p>
              <p className="text-[#666] leading-relaxed mb-8">
                Now available through Clear & Bare's digital consultation platform, Dr. Kosalge brings
                specialist ENT care directly to you — wherever you are in India.
              </p> 
            </motion.div>

            {/* Credentials card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {[
                { label: 'Specialty',     value: 'ENT & Head-Neck Surgery' },
                { label: 'Qualification', value: 'MBBS, MS (ENT), Senior Fellow' },
                { label: 'Fellowships',   value: 'Facial Trauma (Mumbai) · Vertigo (Bangalore)' },
                { label: 'Experience',    value: '5+ Years Clinical Practice' },
                { label: 'Patients',      value: '30,000+ Patients Treated' },
                { label: 'Surgeries',     value: '2,000+ Procedures Performed' },
                { label: 'Consultation',  value: 'Online via Clear & Bare + ADVANCE ENT OPD' },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex gap-4 p-4 bg-[#faf8f5] rounded-xl border border-[#f0ece5]"
                >
                  <div className="w-32 text-[#999] text-sm flex-shrink-0 pt-0.5">{row.label}</div>
                  <div className="text-[#2c2c2c] text-sm">{row.value}</div>
                </div>
              ))}

              <Button
                onClick={handleBookENT}
                className="w-full mt-4 bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full py-6 text-base shadow-xl shadow-[#d4a574]/20 transition-all duration-300 group"
              >
                Book a Consultation with Dr. Kosalge
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── SPECIALTIES ── */}
      <section id="specialties" className="py-20 bg-gradient-to-br from-[#faf8f5] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-[#f9f1e8] text-[#d4a574] border-0 px-4 py-1">Specialties</Badge>
            <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mb-4">What We Treat</h2>
            <p className="text-[#666] max-w-2xl mx-auto text-lg">
              Comprehensive ENT care across the full spectrum of ear, nose, and throat conditions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {specialties.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#f9f1e8] to-[#faf8f5] rounded-xl flex items-center justify-center mb-5">
                  <s.icon className="w-6 h-6 text-[#d4a574]" />
                </div>
                <h3 className="text-xl text-[#2c2c2c] mb-3">{s.title}</h3>
                <p className="text-[#666] mb-5 text-sm leading-relaxed">{s.description}</p>
                <ul className="space-y-2">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-[#666]">
                      <CheckCircle className="w-4 h-4 text-[#d4a574] flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Consultation card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-br from-[#faf8f5] to-white rounded-3xl p-8 md:p-12 shadow-2xl shadow-[#d4a574]/10 border border-gray-100"
          >
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-1">
                <Badge className="mb-4 bg-gradient-to-r from-[#d4a574] to-[#c89464] text-white border-0 px-4 py-1">
                  Expert ENT Consultation
                </Badge>
                <h2 className="text-3xl text-[#2c2c2c] mb-3">What's Included in Your ₹999 Consultation</h2>
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  {[
                    { icon: Stethoscope, title: 'Direct Consultation', sub: 'One-on-one with Dr. Sourabh Kosalge' },
                    { icon: Shield,      title: 'Digital Prescription', sub: 'Valid at any pharmacy across India' },
                    { icon: Activity,    title: 'Treatment Plan',       sub: 'Evidence-based, personalised protocol' },
                    { icon: MessageCircle, title: '7-Day Support',      sub: 'Post-consultation chat support' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3 bg-white p-4 rounded-xl">
                      <item.icon className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-[#2c2c2c] text-sm mb-0.5">{item.title}</div>
                        <div className="text-[#999] text-xs">{item.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={handleBookENT}
                  className="mt-8 bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full px-10 py-6 text-lg shadow-xl shadow-[#d4a574]/30 transition-all duration-300 group"
                >
                  Book ENT Consultation
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-[#f9f1e8] text-[#d4a574] border-0 px-4 py-1">Testimonials</Badge>
            <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mb-4">What Our Patients Say</h2>
            <p className="text-[#666] max-w-2xl mx-auto text-lg">
              Real experiences from patients who found lasting relief
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
  <TestimonialCard
    key={i}
    name={t.name}
    quote={t.quote}
    rating={t.rating}
  />
))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 bg-gradient-to-br from-[#faf8f5] to-white">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-[#f9f1e8] text-[#d4a574] border-0 px-4 py-1">FAQ</Badge>
            <h2 className="text-4xl text-[#2c2c2c] mb-4">Frequently Asked Questions</h2>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left text-[#2c2c2c] hover:bg-[#faf8f5] transition-colors"
                >
                  <span className="pr-4">{faq.q}</span>
                  <ArrowRight
                    className={`w-4 h-4 text-[#d4a574] flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-90' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-[#666] text-sm leading-relaxed border-t border-gray-50">
                    <div className="pt-4">{faq.a}</div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONNECT ── */}
      <section id="connect" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-[#f9f1e8] text-[#d4a574] border-0 px-4 py-1">Connect With Us</Badge>
            <h2 className="text-4xl text-[#2c2c2c] mb-4">Ways to Reach Us</h2>
            <p className="text-[#666] max-w-xl mx-auto">
              Book a consultation, ask a question, or just say hello — we're here for you.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {connects.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all text-center group"
              >
                <div className={`w-14 h-14 ${c.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <c.icon className="w-6 h-6" />
                </div>
                <div className="text-[#2c2c2c] mb-1">{c.label}</div>
                <div className="text-[#999] text-xs mb-4 break-all">{c.value}</div>
                <Button
                  onClick={c.action}
                  variant="outline"
                  className="w-full rounded-full text-sm border-[#e0ddd8] text-[#666] hover:border-[#d4a574] hover:text-[#d4a574] group-hover:border-[#d4a574]"
                >
                  {c.cta}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Business Hours */}
          <div className="bg-[#faf8f5] rounded-2xl p-8 border border-[#f0ece5] text-center max-w-xl mx-auto">
            <Clock className="w-8 h-8 text-[#d4a574] mx-auto mb-4" />
            <h3 className="text-[#2c2c2c] text-xl mb-4">Consultation Hours</h3>
            <div className="space-y-2 text-[#666]">
              <p>Monday – Saturday: 9:00 AM – 6:00 PM IST</p>
              <p>Sunday: Closed</p>
              <p className="italic text-sm mt-3">Typical response time: within 24 hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-gradient-to-br from-[#d4a574] to-[#c89464] relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl md:text-5xl text-white mb-6">
              Ready to Breathe Better?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Book your ENT consultation with Dr. Sourabh Kosalge today. Expert care, clear diagnosis,
              lasting relief — all from home.
            </p>
            <Button
              onClick={handleBookENT}
              className="bg-white text-[#d4a574] hover:bg-gray-50 rounded-full px-10 py-6 text-lg shadow-2xl transition-all duration-300 group"
            >
              Book ENT Consultation — ₹999
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#2c2c2c] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#d4a574] to-[#c89464] rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white">Clear & Bare</h3>
                  <p className="text-xs text-gray-400">Skincare & ENT Excellence</p>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6">
                Your trusted partner in comprehensive healthcare — dermatology, nutrition, and
                now expert ENT care. Accessible, affordable, and evidence-based.
              </p>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/clearandbare_2025"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-[#d4a574] rounded-full flex items-center justify-center transition-colors"
                >
                  <FaInstagram className="w-5 h-5" />
                </a>
                <a
                  href="https://wa.me/918788631381"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-[#d4a574] rounded-full flex items-center justify-center transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-white mb-4">Quick Links</h4>
              <ul className="space-y-3">
                {[
                  { label: 'Home', href: '/' },
                  { label: 'About Dr. Kosalge', href: '#about' },
                  { label: 'Specialties', href: '#specialties' },
                  { label: 'Testimonials', href: '#testimonials' },
                  { label: 'Shop', href: '/shop' },
                ].map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-gray-400 hover:text-[#d4a574] transition-colors flex items-center gap-2 group">
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-400">
                  <Phone className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                  <a href="tel:+918788631381" className="hover:text-[#d4a574] transition-colors">+91 8788631381</a>
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <Mail className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                  <a href="mailto:clearandbare.skincare@gmail.com" className="hover:text-[#d4a574] transition-colors">clearandbare.skincare@gmail.com</a>
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <MapPin className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                  <span>Central Hanuman Road, Beed, Maharashtra 431515</span>
                </li>
              </ul>
            </div>
          </div>

          <Separator className="bg-white/10 mb-8" />

          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>© 2025 Clear & Bare. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="/legal" className="hover:text-[#d4a574] transition-colors">Privacy Policy</a>
              <a href="/legal" className="hover:text-[#d4a574] transition-colors">Terms of Service</a>
              <a href="/legal" className="hover:text-[#d4a574] transition-colors">Cancellation & Refunds</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        className="fixed bottom-8 right-8 z-40"
      >
        <Button
          onClick={handleBookENT}
          className="w-16 h-16 rounded-full bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white shadow-2xl shadow-[#d4a574]/50 hover:scale-110 transition-all duration-300"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
      </motion.div>
    </div>
  );
};

export default ENTPage;
